import React, { useState, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import Select from "react-select";
import {
  Button,
  Input,
  Label,
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import "react-calendar/dist/Calendar.css";
import { postLogin } from "../../helpers/backend_helper";
import { getCheckInDates } from "../../helpers/general";

const ModernCheckInModal = ({ userId }) => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [checkInData, setCheckInData] = useState({});
  const [userIds, setUserId] = useState(userId?.userId || null);
  const [checkInDatesMap, setCheckInDatesMap] = useState([]);

  const handleActionClick = async (user) => {
    setLoading(true);
    try {
      const Payload = {
        act: "user",
        subact: "getUserCheckIn",
        info: {
          userId: userIds,
          month,
          year,
        },
      };
      const res = await postLogin(Payload);
      const checkInDatesMap = getCheckInDates(res?.data);
      
      setCheckInDatesMap(checkInDatesMap);

      setCheckInData(res?.data?.user);
    } catch (e) {
      setCheckInData(null);
    }
    setLoading(false);
  };
  console.log("checkInDatesMap", checkInDatesMap);
  console.log("checkInData", checkInData);

  useEffect(() => {
    handleActionClick(userId);
  }, [month, year, userId]);

  const handleFetchCheckIns = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  };

  const handleMonthChange = (direction) => {
    if (direction === "prev") {
      if (month === 1) {
        setMonth(12);
        setYear(year - 1);
      } else {
        setMonth(month - 1);
      }
    } else {
      // Disable next month navigation - check if trying to go to future month
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      
      // Only allow navigation if the next month is not in the future
      if (nextYear < currentYear || (nextYear === currentYear && nextMonth <= currentMonth)) {
        if (month === 12) {
          setMonth(1);
          setYear(year + 1);
        } else {
          setMonth(month + 1);
        }
      }
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const isDateInFuture = (day, month, year) => {
    const today = new Date();
    const dateToCheck = new Date(year, month - 1, day);
    return dateToCheck > today;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      const hasData = dateKey in checkInDatesMap;
      const isPresent = checkInDatesMap[dateKey];
      const isFutureDate = isDateInFuture(day, month, year);

      days.push(
        <div
          key={day}
          className={`h-10 w-10 rounded-full d-flex items-center justify-center text-sm font-medium transition-all duration-200 hover:scale-110 cursor-pointer ${
            isFutureDate
              ? "bg-white text-gray-400 border border-gray-200 hover:bg-gray-50"
              : hasData
              ? isPresent
                ? "bg-green-500 text-white shadow-lg hover:bg-green-600"
                : "bg-red-500 text-white shadow-lg hover:bg-red-600"
              : "bg-red-500 text-white shadow-lg hover:bg-red-600"
          }`}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const presentDays = Object.values(checkInDatesMap).filter(Boolean).length;
  // Fixed: Use total days in the month instead of just check-in data length
  const totalDays = getDaysInMonth(month, year);
  const attendanceRate =
    totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : 0;

  // Check if next month navigation should be disabled
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const isNextMonthDisabled = (year > currentYear) || (year === currentYear && month >= currentMonth);

  return (
    <>
      <div>
        <Container fluid className="mb-4">
          <Row>
            <Col md={4} className="mb-3 mb-md-0">
              <Label className="form-label fw-semibold text-secondary small">
                Month
              </Label>
              <Select
                value={
                  month ? { value: month, label: monthNames[month - 1] } : null
                }
                onChange={(selectedOption) => setMonth(selectedOption.value)}
                options={monthNames.map((name, index) => {
                  const monthValue = index + 1;
                  const isFutureMonth = year > currentYear || (year === currentYear && monthValue > currentMonth);
                  return {
                    value: monthValue,
                    label: name,
                    isDisabled: isFutureMonth
                  };
                })}
                isDisabled={loading}
                classNamePrefix="react-select"
                isOptionDisabled={(option) => option.isDisabled}
              />
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <Label className="form-label fw-semibold text-secondary small">
                Year
              </Label>
              <Input
                type="number"
                value={year}
                onChange={(e) => {
                  const newYear = Number(e.target.value);
                  // Prevent setting year to future
                  if (newYear <= currentYear) {
                    setYear(newYear);
                    // If setting to current year and month is in future, reset to current month
                    if (newYear === currentYear && month > currentMonth) {
                      setMonth(currentMonth);
                    }
                  }
                }}
                disabled={loading}
                max={currentYear}
              />
            </Col>
            <Col md={4} className="d-flex align-items-end">
             
            </Col>
          </Row>
        </Container>
        {checkInData && (
          <div>
            {/* Today's Status */}
            <Card
              className="mb-4"
              style={{
                background: "linear-gradient(90deg, #f0fdf4 0%, #eff6ff 100%)",
                borderRadius: "1rem",
              }}
            >
              <CardBody className="d-flex align-items-center justify-content-between py-3">
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: checkInData.checkIn ? "#22c55e" : "#ef4444",
                    }}
                  ></div>
                  <span className="fw-semibold text-secondary">
                    Today's Status:
                  </span>
                  <span
                    className={`fw-bold ${
                      checkInData.checkIn ? "text-success" : "text-danger"
                    }`}
                  >
                    {checkInData.checkIn ? "Present" : "Absent"}
                  </span>
                </div>
                {checkInData.checkInTime && (
                  <div className="d-flex align-items-center text-muted small">
                    <Clock size={16} className="me-1" />
                    {new Date(checkInData.checkInTime).toLocaleTimeString()}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Attendance Summary */}
            <Row className="mb-4">
              <Col md={4} xs={12} className="mb-2 mb-md-0">
                <Card
                  className="text-center"
                  style={{ background: "#dcfce7", borderRadius: "0.75rem" }}
                >
                  <CardBody>
                    <div className="h3 fw-bold text-success mb-0">
                      {presentDays}
                    </div>
                    <div className="small text-success">Check-In Days</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4} xs={12} className="mb-2 mb-md-0">
                <Card
                  className="text-center"
                  style={{ background: "#fee2e2", borderRadius: "0.75rem" }}
                >
                  <CardBody>
                    <div className="h3 fw-bold text-danger mb-0">
                      {totalDays - presentDays}
                    </div>
                    <div className="small text-danger">No Check-In Days</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4} xs={12}>
                <Card
                  className="text-center"
                  style={{ background: "#dbeafe", borderRadius: "0.75rem" }}
                >
                  <CardBody>
                    <div className="h3 fw-bold text-primary mb-0">
                      {attendanceRate}%
                    </div>
                    <div className="small text-primary">Percentage</div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Calendar */}
            <Card className="mb-4" style={{ borderRadius: "1rem" }}>
              <CardBody>
                {/* Calendar Header */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  {/* Calendar Header */}
                  <div className="d-flex items-center align-items-center justify-content-between mb-6">
                    <button
                      onClick={() => handleMonthChange("prev")}
                      className="p-2 hover:bg-gray-100 border-0 rounded-full transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {monthNames[month - 1]} {year}
                    </h3>
                    <button
                      onClick={() => handleMonthChange("next")}
                      disabled={isNextMonthDisabled}
                      className={`p-2 border-0 rounded-full transition-colors ${
                        isNextMonthDisabled 
                          ? "text-gray-300 cursor-not-allowed opacity-50" 
                          : "hover:bg-gray-100 text-gray-600"
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 d-flex justify-content-between p-2 gap-2 mb-2">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="text-center  d-flex text-sm font-medium text-gray-500 py-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">{renderCalendar()}</div>
                {/* Legend */}
                <div className="d-flex align-items-center justify-content-center gap-4 mt-4 pt-3 border-top">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#22c55e",
                      }}
                    ></div>
                    <span className="small text-secondary">Check-In</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#ef4444",
                      }}
                    ></div>
                    <span className="small text-secondary">Not Check-In</span>
                  </div>
                 
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default ModernCheckInModal;